import React, { useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { ExternalLink, Github, X, Eye } from 'lucide-react';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const sectionRef = React.useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const projects = [
    {
      id: 1,
      name: 'AssetVerse - Corporate Asset Management System',
      image: 'https://i.ibb.co/N5sDNjY/Asset-Verce.png',
      briefDescription: 'A comprehensive corporate asset management system for tracking and managing company assets.',
      techStack: ['React.js', 'Node.js', 'Stripe Payment', 'Express.js', 'MongoDB', 'JWT Authentication', 'Tailwind CSS'],
      fullDescription: 'AssetVerse is a full-stack web application designed to help corporations efficiently manage their assets. The system provides role-based access control, real-time asset tracking, and comprehensive reporting features.',
      liveLink: 'https://assetverse00.netlify.app/',
      githubLinks: {
        client: 'https://github.com/tahsin-99/AssetVerse-client',
        server: 'https://github.com/tahsin-99/AssetVerse-server'
      },
      challenges: [
        'Implementing complex role-based authentication system',
        'Managing real-time data synchronization across multiple users',
        'Integrating Stripe payment gateway for asset purchases and subscriptions',
        'Optimizing database queries for large asset datasets',
        'Creating responsive design for various screen sizes'
      ],
      improvements: [
        'Add mobile application for field asset management',
        'Implement barcode/QR code scanning functionality',
        'Add advanced analytics and reporting dashboard',
        'Integrate with IoT devices for automatic asset tracking'
      ]
    },
    {
      id: 2,
      name: 'PawMart - Pet Adoption & Supply Portal',
      image: 'https://i.ibb.co/BV6TStpc/pawmart.png',
      briefDescription: 'A comprehensive platform for pet adoption and pet supply shopping.',
      techStack: ['React.js', 'Node.js', 'Express.js', 'MongoDB', 'Firebase Auth', 'Tailwind CSS'],
      fullDescription: 'PawMart combines pet adoption services with an e-commerce platform for pet supplies. Users can browse adoptable pets, complete adoption processes, and purchase pet supplies all in one platform.',
      liveLink: 'https://whimsical-puffpuff-cbcaaf.netlify.app/',
      githubLinks: {
        client: 'https://github.com/tahsin-99/tsp-ph-pet-client',
        server: 'https://github.com/tahsin-99/pet-server'
      },
      challenges: [
        'Managing complex adoption workflow processes',
        'Handling image uploads and optimization for pet photos',
        'Implementing real-time chat between adopters and shelters',
        'Creating intuitive search and filtering for pet listings'
      ],
      improvements: [
        'Add GPS-based pet location services',
        'Implement AI-powered pet matching algorithm',
        'Add veterinary appointment booking system',
        'Create mobile app for better user experience'
      ]
    },
    {
      id: 3,
      name: 'GameHub - Game Download Website',
      image: 'https://i.ibb.co.com/995sdPNX/gamehub.png',
      briefDescription: 'A modern game download platform with user reviews and ratings system.',
      techStack: ['React.js', 'Node.js', 'Express.js', 'MongoDB', 'JWT Auth', 'Tailwind CSS'],
      fullDescription: 'GameHub is a comprehensive game download platform where users can discover, download, and review games. Features include user authentication, game categorization, rating system, and download management.',
      liveLink: 'https://game-hub-e1b8b.web.app/',
      githubLinks: {
        client: 'https://github.com/tahsin-99/GameHub'
      },
      challenges: [
        'Implementing efficient file download management',
        'Creating responsive game gallery with smooth animations',
        'Managing user reviews and rating system',
        'Optimizing image loading for game thumbnails'
      ],
      improvements: [
        'Add game streaming capabilities',
        'Implement social features for gamers',
        'Add game recommendation engine',
        'Create desktop application for better download management'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <section 
      ref={sectionRef}
      id="projects" 
      className="py-16 sm:py-20 lg:py-24 bg-white dark:bg-gray-900 transition-colors duration-300"
    >
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div 
          className="text-center mb-12 sm:mb-16"
          initial={{ opacity: 0, y: -30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Featured <span className="text-primary dark:text-primary-400">Projects</span>
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto text-base sm:text-lg">
            Showcasing my best work in web development and full-stack applications
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {projects.map((project) => (
            <motion.div
              key={project.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group cursor-pointer"
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
              }}
              onClick={() => setSelectedProject(project)}
            >
              {/* Project Image */}
              <div className="relative overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.name}
                  className="w-full h-48 sm:h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Eye className="text-white w-8 h-8" />
                </div>
              </div>

              {/* Project Content */}
              <div className="p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary dark:group-hover:text-primary-400 transition-colors duration-300">
                  {project.name}
                </h3>
                
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mb-4 line-clamp-3">
                  {project.briefDescription}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.techStack.slice(0, 3).map((tech, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-400 rounded-full text-xs font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                  {project.techStack.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs font-medium">
                      +{project.techStack.length - 3} more
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <motion.button
                    className="flex-1 bg-primary hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProject(project);
                    }}
                  >
                    <Eye size={16} />
                    <span className="text-sm">View Details</span>
                  </motion.button>
                  
                  <motion.a
                    href={project.liveLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border border-primary text-primary hover:bg-primary hover:text-white rounded-lg transition-colors duration-200"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={16} />
                  </motion.a>
                  
                  <motion.a
                    href={project.githubLinks.client}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Github size={16} />
                  </motion.a>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Project Modal */}
        <AnimatePresence>
          {selectedProject && (
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedProject(null)}
            >
              <motion.div
                className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="relative">
                  <img 
                    src={selectedProject.image} 
                    alt={selectedProject.name}
                    className="w-full h-48 sm:h-64 object-cover"
                  />
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-200"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 sm:p-8">
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {selectedProject.name}
                  </h3>

                  {/* Tech Stack */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Technology Stack</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.techStack.map((tech, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-400 rounded-full text-sm font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Description</h4>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                      {selectedProject.fullDescription}
                    </p>
                  </div>

                  {/* Challenges */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Challenges Faced</h4>
                    <ul className="space-y-2">
                      {selectedProject.challenges.map((challenge, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="text-primary mt-1">•</span>
                          <span className="text-gray-600 dark:text-gray-300">{challenge}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Future Improvements */}
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Future Improvements</h4>
                    <ul className="space-y-2">
                      {selectedProject.improvements.map((improvement, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="text-primary mt-1">•</span>
                          <span className="text-gray-600 dark:text-gray-300">{improvement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <motion.a
                      href={selectedProject.liveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-primary hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <ExternalLink size={20} />
                      <span>View Live Project</span>
                    </motion.a>
                    
                    {/* GitHub Repository Links */}
                    <div className="flex flex-col sm:flex-row gap-2 flex-1">
                      <motion.a
                        href={selectedProject.githubLinks.client}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 border border-primary text-primary hover:bg-primary hover:text-white px-4 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Github size={20} />
                        <span>Client Code</span>
                      </motion.a>
                      
                      {selectedProject.githubLinks.server && (
                        <motion.a
                          href={selectedProject.githubLinks.server}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 px-4 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Github size={20} />
                          <span>Server Code</span>
                        </motion.a>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Projects;